import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useToast } from '@/components/ui/use-toast'
import { POST_STATUS } from '@/constants/socialPostStatus'
import * as postsApi from '../api/posts'
import type { SocialPost, PostFilters } from '../types'

/**
 * Fetch all posts with optional filters
 */
export function usePosts(filters?: PostFilters) {
    return useQuery({
        queryKey: ['social-posts', filters],
        queryFn: () => postsApi.getPosts(filters)
    })
}

/**
 * Fetch a single post by ID
 */
export function usePost(id: string) {
    return useQuery({
        queryKey: ['social-post', id],
        queryFn: () => postsApi.getPost(id),
        enabled: !!id
    })
}

/**
 * Create a new post
 */
export function useCreatePost() {
    const queryClient = useQueryClient()
    const { toast } = useToast()

    return useMutation({
        mutationFn: async (post: Partial<SocialPost>) => {
            console.log('useCreatePost: Raw post data received:', post)

            // Server-side validation
            if (!post.caption || post.caption.trim().length < 10) {
                throw new Error('Caption must be at least 10 characters long')
            }

            if (!post.profile_id || post.profile_id.trim() === '') {
                throw new Error('Please select a social media profile')
            }

            // Validate scheduled time if provided
            if (post.scheduled_time) {
                const scheduledDate = new Date(post.scheduled_time)
                if (scheduledDate <= new Date()) {
                    throw new Error('Scheduled time must be in the future')
                }
            }

            // Determine status based on scheduled_time
            const status = post.scheduled_time ? POST_STATUS.SCHEDULED : POST_STATUS.DRAFT

            const result = await postsApi.createPost({
                ...post,
                status: post.status || status
            })

            console.log('useCreatePost: Database response:', result)
            return result
        },
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: ['social-posts'] })
            toast({
                title: "✅ Post created successfully!",
                description: "Your post has been saved and is ready to publish.",
            })
        },
        onError: (error: any) => {
            toast({
                title: "❌ Failed to create post",
                description: error.message || "An unexpected error occurred. Please try again.",
                variant: "destructive",
            })
        }
    })
}

/**
 * Update an existing post
 */
export function useUpdatePost() {
    const queryClient = useQueryClient()
    const { toast } = useToast()

    return useMutation({
        mutationFn: async ({ id, ...updates }: Partial<SocialPost> & { id: string }) => {
            // Server-side validation
            if (updates.caption !== undefined && (!updates.caption || updates.caption.trim().length < 10)) {
                throw new Error('Caption must be at least 10 characters long')
            }

            if (updates.profile_id !== undefined && (!updates.profile_id || updates.profile_id.trim() === '')) {
                throw new Error('Please select a social media profile')
            }

            // Validate scheduled time if provided
            if (updates.scheduled_time) {
                const scheduledDate = new Date(updates.scheduled_time)
                if (scheduledDate <= new Date()) {
                    throw new Error('Scheduled time must be in the future')
                }
            }

            return await postsApi.updatePost(id, updates)
        },
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: ['social-posts'] })
            toast({
                title: "✅ Post updated successfully!",
                description: "Your changes have been saved.",
            })
        },
        onError: (error: any) => {
            toast({
                title: "❌ Failed to update post",
                description: error.message || "Unable to save changes. Please try again.",
                variant: "destructive",
            })
        }
    })
}

/**
 * Delete a post
 */
export function useDeletePost() {
    const queryClient = useQueryClient()
    const { toast } = useToast()

    return useMutation({
        mutationFn: (id: string) => postsApi.deletePost(id),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: ['social-posts'] })
            toast({
                title: "✅ Post deleted",
                description: "The post has been removed.",
            })
        },
        onError: (error: any) => {
            toast({
                title: "❌ Failed to delete post",
                description: error.message || "Unable to delete the post. Please try again.",
                variant: "destructive",
            })
        }
    })
}

/**
 * Fetch scheduled posts ready to be published
 */
export function useScheduledPosts() {
    return useQuery({
        queryKey: ['scheduled-posts'],
        queryFn: () => postsApi.getScheduledPosts()
    })
}
